import { UseMutateAsyncFunction, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { PortfolioAnalyticsService } from "@analytics/index";

import { getInsight } from "@api/ipSearch.api";
import { listTechnologies } from "@api/organization.api";
import {
  addCompaniesToTechnology,
  addContactsToTechnology,
  archiveTechnology,
  createTechnologyFromCompanyIds,
  createTechnologyFromContactIds,
  createTechnologyFromPortfolio,
  getPortfolios,
  getTechnology,
  removeCompaniesFromTechnology,
  removeContactsFromTechnology,
  updateTechnology,
} from "@api/portfolio.api";

import C from "@constants/constants";
import routes from "@constants/routes";

import { GetChatRequestType } from "@interfaces/chat.type";
import { SearchType } from "@interfaces/ipsearch.type";
import {
  ArchiveTechnologyResponseType,
  CreateNewTechnologyType,
  CreateTechnologyFromCompanyIdsResponseType,
  CreateTechnologyFromContactIdsResponseType,
  IPortfolioTechnology,
  ITechnology,
  ListTechnologiesType,
  RemoveContactsFromTechnologyResponseType,
} from "@interfaces/portfolio.type";

import { OrganizationStore } from "@store/organizationStore";
import { PopupActions } from "@store/popupStore";

export const usePortfolioQuery = () => {
  const organization = OrganizationStore.getState().organization;
  const queryKey = organization ? ["portfolio", organization.id.toString()] : ["portfolio"];
  if (organization) {
    return useQuery({
      queryFn: async () => listTechnologies(organization.id.toString()),
      queryKey,
      staleTime: C.STALE_TIME,
    });
  } else {
    return useQuery({
      queryFn: async () => getPortfolios(),
      queryKey,
      staleTime: C.STALE_TIME,
    });
  }
};

export const useTechnologyQuery = (technologyId: string | undefined) => {
  const navigate = useNavigate();
  const organization = OrganizationStore.getState().organization;

  return useQuery({
    enabled: !!technologyId, // Avoid running if no ID
    queryFn: async () => {
      try {
        const { body } = await getTechnology(technologyId ?? ""); // TechnologyId exists because enabled=false otherwise
        return body;
      } catch {
        if (organization) {
          navigate(`${routes.ORGANIZATION}/${organization.id}`); // Redirect to the organization page
        } else {
          navigate(`${routes.PORTFOLIO}`); // Redirect to the portfolio page
        }
        throw new Error("Redirecting due to 403 error");
      }
    },
    queryKey: ["technology", technologyId],
    staleTime: C.STALE_TIME,
  });
};

interface IArchivePortfolioInput {
  technologyId: string;
  technologyName: string;
}

export type IArchivePortfolioMutationType = UseMutateAsyncFunction<
  ArchiveTechnologyResponseType,
  Error,
  IArchivePortfolioInput,
  ListTechnologiesType[]
>;

export const useArchiveTechnologyQuery = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;
  return useMutation<ArchiveTechnologyResponseType, Error, IArchivePortfolioInput, ListTechnologiesType[]>({
    mutationFn: async ({ technologyId }) => {
      return await archiveTechnology(technologyId);
    },
    onError: (_, __, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["portfolio"], previousData);
      }
      PopupActions.openNotification({
        content: "Something's wrong. Try again later.",
        title: "Error",
      });
    },
    onMutate: async ({ technologyId }) => {
      let previousPortfolio;
      if (organization) {
        await queryClient.cancelQueries({
          queryKey: ["portfolio", organization.id.toString()],
        });
        previousPortfolio = queryClient.getQueryData([
          "portfolio",
          organization.id.toString(),
        ]) as ListTechnologiesType[];
        queryClient.setQueryData(
          ["portfolio", organization.id.toString()],
          (old: { portfolio: IPortfolioTechnology[] } | IPortfolioTechnology[]) => {
            if (old && "portfolio" in old && Array.isArray(old.portfolio)) {
              // If `old` has a `portfolio` property, and it's an array, filter `old.portfolio`
              return {
                ...old,
                portfolio: old.portfolio.filter((portfolio) => portfolio.id !== technologyId),
              };
            } else if (Array.isArray(old)) {
              // If `old` is directly an array, filter it
              return old.filter((portfolio) => portfolio.id !== technologyId);
            }

            return old;
          },
        );
      } else {
        await queryClient.cancelQueries({
          queryKey: ["portfolio"],
        });
        previousPortfolio = queryClient.getQueryData(["portfolio"]) as ListTechnologiesType[];
        queryClient.setQueryData(["portfolio"], (old: IPortfolioTechnology[]) => {
          return old.filter((portfolio) => portfolio.id !== technologyId);
        });
      }
      return previousPortfolio;
    },
    onSuccess: (_, { technologyName }) => {
      PopupActions.closeModal();
      PopupActions.openAlert({
        duration: 3000,
        message: `${technologyName} deleted.`,
        severity: "success",
      });
    },
  });
};

interface ICreateTechnologyFromCompanyIdsInput {
  companyIds: string[];
  description?: string;
  name: string;
  searchId?: string;
}

export const useCreateTechnologyFromCompanyIdsQuery = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;
  return useMutation<
    CreateTechnologyFromCompanyIdsResponseType,
    Error,
    ICreateTechnologyFromCompanyIdsInput,
    ListTechnologiesType[]
  >({
    mutationFn: async ({ companyIds, description, name, searchId }) => {
      return await createTechnologyFromCompanyIds(companyIds, name, description, searchId);
    },
    onError: (_, __, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["portfolio"], previousData);
      }
      PopupActions.openNotification({
        content: "There was an issue creating a new portfolio item. Please try again.",
        title: "Error",
      });
    },
    onSuccess: async (newTechnology, { companyIds, name }) => {
      const { body } = await getTechnology(newTechnology.id);
      queryClient.setQueryData(["technology", newTechnology.id], body);

      if (organization) {
        const newPortfolio = await listTechnologies(organization.id.toString());
        queryClient.setQueryData(["portfolio", organization.id.toString()], (old: IPortfolioTechnology[]) => {
          const oldArray = Array.isArray(old) ? old : []; // Ensure old is an array
          return [newPortfolio[0], ...oldArray];
        });
      } else {
        queryClient.setQueryData(["portfolio"], (old: IPortfolioTechnology[]) => [newTechnology, ...old]);
      }

      PopupActions.openAlert({
        duration: 4000,
        message: `${name} created.`,
        severity: "success",
      });
      PortfolioAnalyticsService.createTechnology(name, newTechnology.id, companyIds.length, 0);
    },
  });
};

interface IAddCompaniesToTechnologyInput {
  companyIds: string[];
  searchId?: string;
  technologyId: string;
  technologyName: string;
}

export const useAddCompaniesToTechnologyQuery = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;

  return useMutation<void, Error, IAddCompaniesToTechnologyInput, IPortfolioTechnology[]>({
    mutationFn: async ({ companyIds, searchId, technologyId }) => {
      await addCompaniesToTechnology(technologyId, companyIds, searchId);
    },
    onError: () => {
      PopupActions.openNotification({
        content: "There was an issue with the connection. Please try again.",
        title: "Error",
      });
    },
    onMutate: ({ companyIds, technologyId }) => {
      //update portfolio item with new company count
      let portfolios: IPortfolioTechnology[] | undefined;

      if (organization) {
        // When organization is present, expect `portfolio` to be an array within the result
        const data = queryClient.getQueryData<{ portfolio: IPortfolioTechnology[] } | undefined>([
          "portfolio",
          organization.id.toString(),
        ]);

        portfolios = data && "portfolio" in data && Array.isArray(data.portfolio) ? data.portfolio : undefined;
      } else {
        // When no organization is present, expect `portfolios` to be an array directly
        portfolios = queryClient.getQueryData<IPortfolioTechnology[] | undefined>(["portfolio"]);
      }

      const updatedPortfolio = portfolios?.map((item) => {
        if (item.id === technologyId) {
          return {
            ...item,
            numCompanies: (Number.parseInt(item.numCompanies) + companyIds.length).toString(),
          };
        }
        return item;
      });
      if (organization) {
        queryClient.setQueryData(["portfolio", organization.id.toString()], updatedPortfolio);
      } else {
        queryClient.setQueryData(["portfolio"], updatedPortfolio);
      }
      return queryClient.getQueryData(["portfolio"]) as IPortfolioTechnology[];
    },
    onSuccess: async (_, { companyIds, technologyId, technologyName }) => {
      await queryClient.invalidateQueries({
        queryKey: ["technology", technologyId],
      });
      await queryClient.refetchQueries({
        queryKey: ["technology", technologyId],
      });

      void queryClient.invalidateQueries({
        queryKey: ["portfolio"],
      });

      PopupActions.openAlert({
        duration: 2000,
        message: `Companies added to ${technologyName}.`,
        severity: "success",
      });
      PortfolioAnalyticsService.addToTechnology(technologyName, technologyId, companyIds.length, 0);
    },
  });
};

interface ICreateTechnologyFormPortfolio {
  description?: string;
  name: string;
  organizationId?: string;
}

export const useCreateTechnologyFromPortfolio = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;
  const navigate = useNavigate();

  return useMutation<CreateNewTechnologyType, Error, ICreateTechnologyFormPortfolio, CreateNewTechnologyType[]>({
    mutationFn: async ({ description, name }) => {
      return await createTechnologyFromPortfolio(name, description);
    },
    onError: (_, __, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["portfolio"], previousData);
      }
      PopupActions.openNotification({
        content: "There was an issue creating a new portfolio item. Please try again.",
        title: "Error",
      });
    },
    onSuccess: async (newTechnology, { name }) => {
      const { body } = await getTechnology(newTechnology.id);
      queryClient.setQueryData(["technology", newTechnology.id], body);
      let newPortfolio;
      if (organization) {
        newPortfolio = await listTechnologies(organization.id.toString());
        queryClient.setQueryData(["portfolio", organization.id.toString()], newPortfolio);
      } else {
        newPortfolio = await getPortfolios();
        queryClient.setQueryData(["portfolio"], newPortfolio);
      }
      PopupActions.openAlert({
        duration: 4000,
        message: `${name} created.`,
        severity: "success",
      });
      PopupActions.closeModal();
      navigate(`${routes.TECHNOLOGY}/${newTechnology.id}/overview`);
      PortfolioAnalyticsService.createTechnology(name, newTechnology.id, 0, 0);
    },
  });
};

interface ICreateTechnologyFromContactIdsInput {
  contactIds: string[];
  description?: string;
  name: string;
  searchId?: string;
}

export const useCreateTechnologyFromContactIdsQuery = () => {
  const queryClient = useQueryClient();
  return useMutation<
    CreateTechnologyFromContactIdsResponseType,
    Error,
    ICreateTechnologyFromContactIdsInput,
    CreateTechnologyFromContactIdsResponseType[]
  >({
    mutationFn: async ({ contactIds, description, name, searchId }) => {
      console.log("SEARCH ID", searchId);
      return await createTechnologyFromContactIds(contactIds, name, description, searchId);
    },
    onError: (_, __, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["portfolio"], previousData);
      }
      PopupActions.openNotification({
        content: "There was an issue creating a new portfolio item. Please try again.",
        title: "Error",
      });
    },
    onSuccess: async (newTechnology, { contactIds, name }) => {
      const newPortfolio = await getPortfolios();
      const { body } = await getTechnology(newTechnology.id);
      queryClient.setQueryData(["portfolio"], newPortfolio);
      queryClient.setQueryData(["technology", newTechnology.id], body);
      PopupActions.openAlert({
        duration: 4000,
        message: `${name} created.`,
        severity: "success",
      });
      PortfolioAnalyticsService.createTechnology(name, newTechnology.id, 0, contactIds.length);
    },
  });
};

interface IAddContactsToTechnologyInput {
  contactIds: string[];
  searchId?: string;
  technologyId: string;
  technologyName: string;
}

export const useAddContactToTechnology = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;

  return useMutation<void, Error, IAddContactsToTechnologyInput, IPortfolioTechnology[]>({
    mutationFn: async ({ contactIds, searchId, technologyId }) => {
      await addContactsToTechnology(technologyId, contactIds, searchId);
    },
    onError: () => {
      PopupActions.openNotification({
        content: "There was an issue with the connection. Please try again.",
        title: "Error",
      });
    },
    onSuccess: async (_, { contactIds, technologyId, technologyName }) => {
      const newPortfolio = await getPortfolios();
      if (organization) {
        queryClient.setQueryData(["portfolio", organization.id.toString()], newPortfolio);
      } else {
        queryClient.setQueryData(["portfolio"], newPortfolio);
      }
      await queryClient.invalidateQueries({
        queryKey: ["technology", technologyId],
      });
      await queryClient.refetchQueries({
        queryKey: ["technology", technologyId],
      });

      void queryClient.invalidateQueries({
        queryKey: ["portfolio"],
      });

      PopupActions.openAlert({
        duration: 2000,
        message: `Contacts added to ${technologyName}.`,
        severity: "success",
      });
      PortfolioAnalyticsService.addToTechnology(technologyName, technologyId, 0, contactIds.length);
    },
  });
};

interface IRemoveContactsFromTechnologyInput {
  contactIds: string[];
  technologyId: string;
}

export const useRemoveContactsFromTechnologyQuery = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;

  return useMutation<RemoveContactsFromTechnologyResponseType, Error, IRemoveContactsFromTechnologyInput, ITechnology>({
    mutationFn: async ({ contactIds, technologyId }): Promise<RemoveContactsFromTechnologyResponseType> => {
      return await removeContactsFromTechnology(contactIds, technologyId);
    },
    onError: (_, __, technology) => {
      PopupActions.openNotification({
        content: "Something's wrong. Try again later.",
        title: "Error",
      });

      if (technology) {
        queryClient.setQueryData(["technology", technology.id], technology);
      }
    },
    onMutate: ({ contactIds, technologyId }) => {
      const technologies = queryClient.getQueryData(["technology", technologyId]) as ITechnology;

      // If `technologies` is undefined, return early to ensure type safety
      if (!technologies) {
        return;
      }

      const updatedContacts = technologies.contacts?.filter((contact) => !contactIds.includes(contact.id)) || [];

      const updatedTechnology = {
        ...technologies,
        contacts: updatedContacts,
      };

      queryClient.setQueryData(["technology", technologyId], updatedTechnology);
      return technologies;
    },
    onSuccess: (technology, { contactIds }) => {
      const portfolioData = organization
        ? queryClient.getQueryData<{ portfolio?: IPortfolioTechnology[] }>(["portfolio", organization.id.toString()])
        : queryClient.getQueryData<IPortfolioTechnology[]>(["portfolio"]);

      // Ensure portfolio is always an array
      const portfolio: IPortfolioTechnology[] = Array.isArray(portfolioData)
        ? portfolioData
        : portfolioData?.portfolio ?? [];

      if (portfolio) {
        const updatedPortfolio = portfolio.map((item) => {
          if (item.id === technology.id) {
            return {
              ...item,
              numContacts: (Number.parseInt(item.numContacts, 10) - contactIds.length).toString(),
            };
          }
          return item;
        });

        if (organization) {
          queryClient.setQueryData(["portfolio", organization.id.toString()], updatedPortfolio);
        } else {
          queryClient.setQueryData(["portfolio"], updatedPortfolio);
        }
      }

      queryClient.setQueryData(["technology", technology.id], technology);

      void queryClient.invalidateQueries({
        queryKey: ["portfolio"],
      });

      PopupActions.openAlert({
        duration: 2000,
        message: `${contactIds.length > 1 ? "Contacts" : "Contact"} removed.`,
        severity: "success",
      });
    },
  });
};

interface IRemoveCompaniesFromTechnologyInput {
  companyIds: string[];
  technologyId: string;
}

export const useRemoveCompaniesFromTechnologyQuery = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;

  return useMutation<void, Error, IRemoveCompaniesFromTechnologyInput, ITechnology>({
    mutationFn: async ({ companyIds, technologyId }) => {
      await removeCompaniesFromTechnology(companyIds, technologyId);
    },
    onError: (_, __, technology) => {
      PopupActions.openNotification({
        content: "There was an issue with the connection. Please try again.",
        title: "Error",
      });

      if (technology) {
        queryClient.setQueryData(["technology", technology.id], technology);
      }
    },
    onMutate: ({ companyIds, technologyId }) => {
      const technology = queryClient.getQueryData(["technology", technologyId]) as ITechnology;
      const updatedCompanies = technology.companies.filter((company) => !companyIds.includes(company.id));

      const updatedContacts = technology.contacts.filter(
        (contact) => !companyIds.includes(contact.companyId || "TODO: MAKE COMPANY ID NOT NULLABLE"),
      );

      const updatedTechnology = {
        ...technology,
        companies: updatedCompanies,
        contacts: updatedContacts,
      };

      queryClient.setQueryData(["technology", technologyId], updatedTechnology);

      return technology;
    },
    onSuccess: async (_, { companyIds, technologyId }, technology) => {
      const portfolioData = organization
        ? queryClient.getQueryData<{ portfolio?: IPortfolioTechnology[] }>(["portfolio", organization.id.toString()])
        : queryClient.getQueryData<IPortfolioTechnology[]>(["portfolio"]);

      // Ensure portfolio is always an array
      const portfolio: IPortfolioTechnology[] = Array.isArray(portfolioData)
        ? portfolioData
        : portfolioData?.portfolio ?? [];

      const contactsThatBelongToCompany = technology.contacts
        .filter((contact) => {
          return companyIds.includes(contact.companyId || "TODO: MAKE COMPANY ID NOT NULLABLE");
        })
        .map((contact) => contact.id);

      if (contactsThatBelongToCompany.length > 0) {
        await removeContactsFromTechnology(contactsThatBelongToCompany, technologyId);
      }

      if (organization) {
        void queryClient.invalidateQueries({
          queryKey: ["portfolio", organization.id.toString()],
        });
      } else {
        void queryClient.invalidateQueries({
          queryKey: ["portfolio"],
        });
      }

      const updatedPortfolio = portfolio.map((item) => {
        if (item.id === technologyId) {
          return {
            ...item,
            numCompanies: (Number.parseInt(item.numCompanies) - companyIds.length).toString(),
            numContacts: (Number.parseInt(item.numContacts) - contactsThatBelongToCompany.length).toString(),
          };
        }
        return item;
      });

      queryClient.setQueryData(["portfolio"], updatedPortfolio);

      PopupActions.openAlert({
        duration: 2000,
        message: `${companyIds.length > 1 ? "Companies" : "Company"} removed.`,
        severity: "success",
      });
    },
  });
};

interface IUpdateTechnology {
  newDescription?: string;
  newName: string;
  technologyId: string;
}

export const useUpdateTechnologyQuery = () => {
  const queryClient = useQueryClient();
  const organization = OrganizationStore.getState().organization;
  return useMutation<void, Error, IUpdateTechnology, ITechnology>({
    mutationFn: async ({ newDescription, newName, technologyId }) => {
      await updateTechnology(technologyId, newName, newDescription);
    },
    onError: (_, __, technology) => {
      if (technology) {
        queryClient.setQueryData(["technology", technology.id], technology);
      }
      PopupActions.openAlert({
        duration: 4000,
        message: "Error updating Technology. Please try again.",
        severity: "error",
      });
    },
    onMutate: ({ newName, technologyId }) => {
      const technology = queryClient.getQueryData(["technology", technologyId]) as ITechnology;
      queryClient.setQueryData(["technology", technologyId], {
        ...technology,
        name: newName,
      });

      return technology;
    },
    onSuccess: (_, { newDescription, newName, technologyId }) => {
      const portfolioData = organization
        ? queryClient.getQueryData<{ portfolio?: IPortfolioTechnology[] }>(["portfolio", organization.id.toString()])
        : queryClient.getQueryData<IPortfolioTechnology[]>(["portfolio"]);

      // Ensure portfolio is always an array
      const portfolio: IPortfolioTechnology[] = Array.isArray(portfolioData)
        ? portfolioData
        : portfolioData?.portfolio ?? [];

      const updatedPortfolio = portfolio.map((item) => (item.id === technologyId ? { ...item, name: newName } : item));
      PortfolioAnalyticsService.updateTechnology(technologyId, newName, newDescription);

      if (organization) {
        queryClient.setQueryData(["portfolio", organization.id.toString()], updatedPortfolio);
      } else {
        queryClient.setQueryData(["portfolio"], updatedPortfolio);
      }

      if (organization) {
        void queryClient.invalidateQueries({
          queryKey: ["portfolio", organization.id.toString()],
        });
      } else {
        void queryClient.invalidateQueries({
          queryKey: ["portfolio"],
        });
      }

      PopupActions.openAlert({
        duration: 4000,
        message: "Technology  updated.",
        severity: "success",
      });
    },
  });
};

export const useFetchCompanyInsight = () => {
  const queryClient = useQueryClient();
  return useMutation<
    SearchType,
    Error,
    {
      companyId: string;
      messages?: string[];
      model: GetChatRequestType["model"];
      searchId: string;
      technologyId: string;
    },
    ITechnology
  >({
    mutationFn: async ({ companyId, messages, model, searchId }) => {
      return await getInsight(searchId, companyId, model, messages);
    },
    onSuccess: async (_, { technologyId }) => {
      await queryClient.invalidateQueries({
        queryKey: ["technology", technologyId],
      });

      PopupActions.openAlert({
        duration: 4000,
        message: "AI Insight updated.",
        severity: "success",
      });
    },
  });
};
